import { inject, Service } from "@angular/core";

import {
  DataEntryMetricModel,
  DimensionValue,
  EntityModelType,
  MainResponseModel,
  MetricDimension,
  MonitoredKpiModelType,
  ParentMetric,
  PropTypeModelType,
} from "../models";
import { DataEntryMetricResponse, MetricBreakdownResponse } from "../models/metrics-model";
import { MainService } from "./main-service";

@Service()
export class MetricApiService {
  private readonly _mainService = inject(MainService);

  public saveMetric(isEdit: boolean, data: Partial<DataEntryMetricModel>) {
    return this._mainService.sendRequest<MainResponseModel<number>>({
      method: "POST",
      url: isEdit ? `libmetric/update?isFinish=true` : `libmetric/create?isFinish=true`,
      data,
    });
  }

  public updateDataEntryMetrics(data: Partial<DataEntryMetricResponse>[]) {
    return this._mainService.sendRequest<MainResponseModel<null>>({
      method: "POST",
      url: `dataentry/updateMetricEntry`,
      data,
    });
  }

  public deleteMetric(id: number) {
    return this._mainService.sendRequest<MainResponseModel<number>>({
      method: "DELETE",
      url: `libmetric/deleteById/${id}`,
    });
  }

  public deleteDataEntry(id: number) {
    return this._mainService.sendRequest<MainResponseModel<null>>({
      method: "DELETE",
      url: `dataentry/deleteById/${id}`,
    });
  }

  public getAllMetrics() {
    return this._mainService.sendRequest<MainResponseModel<DataEntryMetricModel[]>>({
      method: "POST",
      url: `libmetric/getallv2`,
      data: {},
    });
  }

  public getRelatedKpis(id: number) {
    return this._mainService.sendRequest<MainResponseModel<MonitoredKpiModelType[]>>({
      method: "GET",
      url: `libmetric/getRelatedKpis?metricId=${id}`,
    });
  }

  public getDataEntryByMetricId(id: number) {
    return this._mainService.sendRequest<MainResponseModel<DataEntryMetricResponse[]>>({
      method: "GET",
      url: `dataentry/getByMetricId/${id}`,
    });
  }

  public getById(id: number) {
    return this._mainService.sendRequest<MainResponseModel<DataEntryMetricModel>>({
      method: "GET",
      url: `libmetric/getbyid/${id}`,
    });
  }

  public getAllMonitoring(limit?: number, offset?: number) {
    const maxValue = 9_999_999;

    const qs = new URLSearchParams();
    qs.set("limit", `${limit ?? maxValue}`);
    qs.set("offset", `${offset ?? 0}`);

    return this._mainService.sendRequest<MainResponseModel<DataEntryMetricModel[]>>({
      method: "POST",
      url: `libmetric/getAllMonitoring?${qs}`,
      data: {},
    });
  }

  public getAllMonitoringV2(limit?: number, offset?: number, sponsorEntityId?: number) {
    const maxValue = 9_999_999;

    const qs = new URLSearchParams();
    qs.set("limit", `${limit ?? maxValue}`);
    qs.set("offset", `${offset ?? 0}`);
    if (sponsorEntityId) qs.set("sponsorEntityId", `${sponsorEntityId}`);

    return this._mainService.sendRequest<MainResponseModel<DataEntryMetricModel[]>>({
      method: "POST",
      url: `libmetric/getAllMonitoringV2?${qs}`,
      data: {},
    });
  }

  public getAvailableDashboardTypes() {
    return this._mainService.sendRequest<MainResponseModel<PropTypeModelType[]>>({
      method: "GET",
      url: `libmetric/availableDashboardType`,
    });
  }

  public getParentMetrics(entityId?: number) {
    return this._mainService.sendRequest<MainResponseModel<ParentMetric[]>>({
      method: "GET",
      url: `libmetric/getParentMetrics${entityId ? "?entityId=" + entityId.toString() : ""}`,
    });
  }

  public getDimensionsByParentMetric(parentMetricId: number) {
    return this._mainService.sendRequest<MainResponseModel<MetricDimension[]>>({
      method: "GET",
      url: `libmetric/getDimensionsByParentMetric?parentMetricId=${parentMetricId}`,
    });
  }

  public getDimensionsValues(dimensionsValues: number[]) {
    const qs = new URLSearchParams();

    dimensionsValues.forEach((e) => qs.append("dimensionId", e.toString()));

    return this._mainService.sendRequest<MainResponseModel<DimensionValue[]>>({
      method: "GET",
      url: `libmetric/getDimensionsValues?${qs}`,
    });
  }

  public getParentMetricEntities() {
    return this._mainService.sendRequest<MainResponseModel<EntityModelType[]>>({
      method: "GET",
      url: `libmetric/getParentMetricEntities`,
    });
  }

  public getMetricBreakdown(
    periodId: number,
    kpiId: number | undefined,
    metricId: number | undefined,
    dimensionsValues: number[] | undefined
  ) {
    const qs = new URLSearchParams();

    qs.set("periodId", periodId.toString());
    if (kpiId) qs.set("kpiId", kpiId.toString());
    if (metricId) qs.set("libMetricd", metricId.toString());

    dimensionsValues?.forEach((e) => qs.append("dimensionValueId", e.toString()));

    return this._mainService.sendRequest<MainResponseModel<MetricBreakdownResponse[]>>({
      method: "GET",
      url: `libmetric/dimension-values?${qs}`,
    });
  }
}
